import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import type { NormalizedApiError } from '@/services/types';
import { onpremApi } from '../api/onpremApi';
import type {
	ActivationToken,
	OnpremInstallation,
	OnpremTenant,
	SeatSyncResult,
} from '../types';

/** How often the seat sync state is re-read (the install syncs every 5 min). */
const SYNC_STATUS_REFRESH_MS = 60_000;

/**
 * Deployment mode of the API. Fixed for the life of the process, so it is
 * read once. On failure (an older backend without the route) the CMS acts
 * as the SaaS, which is what it was before.
 */
export function useDeployment() {
	return useQuery({
		queryKey: queryKeys.onprem.deployment(),
		queryFn: ({ signal }) => onpremApi.deployment(signal),
		staleTime: Number.POSITIVE_INFINITY,
		gcTime: Number.POSITIVE_INFINITY,
		retry: 1,
	});
}

/** True on an on-prem install; false on the SaaS or while unknown. */
export function useIsOnprem(): boolean {
	return useDeployment().data?.mode === 'onprem';
}

/** Seat sync state of this install (on-prem admins only). */
export function useSeatSyncStatus(enabled: boolean) {
	return useQuery({
		queryKey: queryKeys.onprem.syncStatus(),
		queryFn: ({ signal }) => onpremApi.syncStatus(signal),
		enabled,
		refetchInterval: SYNC_STATUS_REFRESH_MS,
		retry: false,
	});
}

export function useSyncSeatsNow() {
	const queryClient = useQueryClient();
	return useMutation<SeatSyncResult, NormalizedApiError, void>({
		mutationFn: () => onpremApi.syncNow(),
		onSuccess: (result) => {
			queryClient.setQueryData(queryKeys.onprem.syncStatus(), result.status);
		},
		// A sync may add or remove seat codes.
		onSettled: () =>
			queryClient.invalidateQueries({ queryKey: queryKeys.seats.all }),
	});
}

/** Every on-prem tenant with its installation (platform admins, SaaS). */
export function useOnpremTenants(enabled: boolean) {
	return useQuery({
		queryKey: queryKeys.onprem.tenants(),
		queryFn: ({ signal }) => onpremApi.tenants(signal),
		enabled,
		retry: false,
	});
}

function useInvalidateOnpremTenants() {
	const queryClient = useQueryClient();
	return () =>
		Promise.all([
			queryClient.invalidateQueries({ queryKey: queryKeys.onprem.tenants() }),
			// Hosting changes how the tenant's seats are counted.
			queryClient.invalidateQueries({ queryKey: queryKeys.seats.all }),
		]);
}

export function useMarkOnprem() {
	const invalidate = useInvalidateOnpremTenants();
	return useMutation<OnpremTenant, NormalizedApiError, string>({
		mutationFn: (tenantId) => onpremApi.markOnprem(tenantId),
		onSettled: invalidate,
	});
}

export function useIssueActivationToken() {
	const invalidate = useInvalidateOnpremTenants();
	return useMutation<ActivationToken, NormalizedApiError, string>({
		mutationFn: (tenantId) => onpremApi.issueActivationToken(tenantId),
		onSettled: invalidate,
	});
}

export function useRevokeInstallation() {
	const invalidate = useInvalidateOnpremTenants();
	return useMutation<
		OnpremInstallation,
		NormalizedApiError,
		{ tenantId: string; installationId: string }
	>({
		mutationFn: ({ tenantId, installationId }) =>
			onpremApi.revokeInstallation(tenantId, installationId),
		onSettled: invalidate,
	});
}
